import { CardMedia } from '@mui/material';
import { Box, styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  diffListAgainstPublic, getEntityHeatBuzz, getListCoverImageUrl, mergePublicAndPrivateItems,
  shareActionTitle, shareChangesState,
} from '../common/ItemTools';
import { selectResolveShareTags } from '../redux/features/tags/tagsSlice';
import {
  selectSelectionMode, selectSelectedIds, toggleSelection,
} from '../redux/features/context/contextSlice';
import listLogo from '../resources/list.png';
import SelectionOverlay from './SelectionOverlay';
import HeatBuzzBadge from './common/HeatBuzzBadge';
import VerdictBar, { useVerdictTransition } from './common/VerdictBar';
import CardIdentity from './common/card/CardIdentity';
import useCardMenu from './common/card/useCardMenu';
import useCardDensity, { CardDensityProvider } from './common/card/useCardDensity';
import buildEntityMenuOptions from './common/card/entityMenu';
import { entityFacts } from './common/card/facts';
import CardFooter from './common/card/CardFooter';
import CardFavoriteAction from './common/card/CardFavoriteAction';
import CardShareAction from './common/card/CardShareAction';
import {
  ActionsBox, CompactActionButton, EntityCard, VerdictRow,
} from './common/card/styles';

// A list is drawn as a stack: two sheets peeking out behind the card, so it
// reads as a container of items rather than one more item.
const StackedCardWrap = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  width: '100%',
  paddingTop: theme.spacing(2),
  paddingRight: theme.spacing(2),
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[1],
  },
  '&::before': {
    top: 0,
    right: 0,
    bottom: theme.spacing(2),
    left: theme.spacing(2),
  },
  '&::after': {
    top: theme.spacing(1),
    right: theme.spacing(1),
    bottom: theme.spacing(1),
    left: theme.spacing(1),
  },
}));

const Cover = styled(CardMedia)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  cursor: 'pointer',
  borderRadius: theme.spacing(1),
}));

export default function ListInfoCard(props) {
  const {
    listInfo,
    onListOpen, onListEdit, onListShare, onListVerdict,
    onListDelete, onListUnshare, onListFavorite,
  } = props;

  const dispatch = useDispatch();
  const { cardRef, shows } = useCardDensity();
  const selectionMode = useSelector(selectSelectionMode);
  const selectedIds = useSelector(selectSelectedIds);
  const isSelected = selectedIds.includes(listInfo.id);
  const onToggleSelect = () => dispatch(toggleSelection(listInfo.id));

  const moderating = Boolean(onListVerdict);
  const showVerdictBar = moderating && !selectionMode;
  const handleDecide = React.useCallback(
    (kind, reason) => onListVerdict && onListVerdict(listInfo, kind, reason),
    [onListVerdict, listInfo],
  );
  const { decide, sx: verdictSx } = useVerdictTransition(handleDecide);

  const listInfoCombined = mergePublicAndPrivateItems(listInfo);
  const stats = listInfoCombined.stats || {};
  const { heatScore, buzzScore } = getEntityHeatBuzz(listInfoCombined);

  const resolveShareTags = useSelector(selectResolveShareTags);
  const hasPublicData = Boolean(listInfo.publicData);
  const shareDiff = React.useMemo(
    () => diffListAgainstPublic(listInfo, resolveShareTags(listInfo.privateData?.tagIds)),
    [listInfo, resolveShareTags],
  );

  const authorName = listInfoCombined.authors
    && listInfoCombined.authors[0]
    && listInfoCombined.authors[0].name;
  const facts = entityFacts({ lead: authorName, stats, details: shows('factDetails') });

  const menuOptions = buildEntityMenuOptions({
    target: listInfo,
    hasPrivateData: Boolean(listInfoCombined.hasPrivateData),
    hasPublicData: Boolean(listInfoCombined.hasPublicData),
    isFavorite: Boolean(listInfoCombined.isFavorite),
    shareDiff,
    moderating,
    collapsed: !shows('actions'),
    onEdit: onListEdit,
    onDelete: onListDelete,
    onUnshare: onListUnshare,
    onShare: onListShare,
    onFavorite: onListFavorite,
  });
  const { open: openMenu, menu } = useCardMenu(menuOptions);

  const canShare = onListShare && !moderating && listInfoCombined.hasPrivateData;
  const shareIsMarker = hasPublicData && shareChangesState(shareDiff) === 'none';

  return (
    <CardDensityProvider shows={shows}>
      <StackedCardWrap sx={moderating ? verdictSx : undefined}>
        {/* Lifted over the two sheets the wrapper draws behind it. */}
        <EntityCard raised ref={cardRef} sx={{ zIndex: 1 }}>
          <SelectionOverlay
            selectionMode={selectionMode}
            selected={isSelected}
            onToggle={onToggleSelect}
          />
          <CardIdentity
            onOpen={() => onListOpen && onListOpen(listInfo)}
            title={listInfoCombined.title}
            facts={facts}
            excerpt={shows('excerpt') ? listInfoCombined.description : null}
            media={(
              <Cover
                image={getListCoverImageUrl(listInfoCombined) || listLogo}
                title={listInfoCombined.title}
              />
            )}
            overlay={shows('heatBadge') ? <HeatBuzzBadge heat={heatScore} buzz={buzzScore} /> : null}
          />
          <CardFooter>
            <ActionsBox>
              {canShare && (shareIsMarker ? shows('markers') : shows('actions')) && (
                <CardShareAction
                  hasPublicData={hasPublicData}
                  changes={shareChangesState(shareDiff)}
                  title={shareActionTitle(hasPublicData, shareDiff)}
                  onShare={() => onListShare(listInfo)}
                />
              )}
              {(onListFavorite ? shows('actions') : shows('markers')) && (
                <CardFavoriteAction
                  isFavorite={Boolean(listInfoCombined.isFavorite)}
                  onToggle={onListFavorite && (() => onListFavorite(listInfo))}
                />
              )}
              {menuOptions.length > 0 && !showVerdictBar && shows('menu') && (
                <CompactActionButton onClick={openMenu}>
                  <MoreVertIcon />
                </CompactActionButton>
              )}
            </ActionsBox>
          </CardFooter>
          {showVerdictBar && (
            <VerdictRow>
              <VerdictBar onDecide={decide} menuOptions={menuOptions} />
            </VerdictRow>
          )}
          {menu}
        </EntityCard>
      </StackedCardWrap>
    </CardDensityProvider>
  );
}

ListInfoCard.propTypes = {
  listInfo: PropTypes.object.isRequired,
  onListOpen: PropTypes.func,
  onListEdit: PropTypes.func,
  onListShare: PropTypes.func,
  onListVerdict: PropTypes.func,
  onListDelete: PropTypes.func,
  onListUnshare: PropTypes.func,
  onListFavorite: PropTypes.func,
};

ListInfoCard.defaultProps = {
  onListOpen: null,
  onListEdit: null,
  onListShare: null,
  onListVerdict: null,
  onListDelete: null,
  onListUnshare: null,
  onListFavorite: null,
};
